import { useRef, useState, useEffect, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

interface LightningEffectProps {
  intensity?: number;
  areaSize: [number, number];
  color?: 'white' | 'blue' | 'red';
  minInterval?: number;
  maxInterval?: number;
  onStrike?: () => void;
  // Position du joueur pour que les éclairs tombent près de lui
  playerPosition?: [number, number, number];
  // Distance max autour du joueur pour les éclairs (défaut: 5)
  strikeRadius?: number;
}

interface LightningSegment {
  start: THREE.Vector3;
  end: THREE.Vector3;
  thickness: number;
}

// Generate a zigzag lightning bolt as segments
function generateLightningSegments(
  startX: number,
  startZ: number,
  segments: number = 8
): LightningSegment[] {
  const result: LightningSegment[] = [];
  const startY = 12;
  const endY = 0.5;
  const segmentHeight = (startY - endY) / segments;

  let currentX = startX;
  let currentZ = startZ;
  let currentY = startY;

  for (let i = 0; i < segments; i++) {
    const nextY = currentY - segmentHeight;
    const offsetScale = 0.5 * (1 - i / segments);
    const nextX = currentX + (Math.random() - 0.5) * offsetScale;
    const nextZ = currentZ + (Math.random() - 0.5) * offsetScale;

    result.push({
      start: new THREE.Vector3(currentX, currentY, currentZ),
      end: new THREE.Vector3(nextX, nextY, nextZ),
      thickness: 0.04 * (1 - i / segments * 0.5), // Thinner at the bottom
    });

    currentX = nextX;
    currentY = nextY;
    currentZ = nextZ;
  }

  return result;
}

export function LightningEffect({
  intensity = 0.7,
  areaSize,
  color = 'white',
  minInterval = 4000,
  maxInterval = 12000,
  onStrike,
  playerPosition,
  strikeRadius = 5,
}: LightningEffectProps) {
  const groupRef = useRef<THREE.Group>(null);
  const flashPlaneRef = useRef<THREE.Mesh>(null);
  const explosionRef = useRef<THREE.Points>(null);
  const { camera } = useThree();

  const [isStriking, setIsStriking] = useState(false);
  const [strikePosition, setStrikePosition] = useState<[number, number]>([0, 0]);
  const [lightningSegments, setLightningSegments] = useState<LightningSegment[]>([]);
  const [visibleSegments, setVisibleSegments] = useState(0);

  const strikeStartTime = useRef(0);
  const nextStrikeTime = useRef(0);
  const strikePhase = useRef<'descend' | 'flash' | 'explosion' | 'fade'>('descend');
  const flashIntensity = useRef(0);

  // Color configurations
  const colorConfig = useMemo(() => {
    switch (color) {
      case 'blue':
        return { main: '#00DDFF', glow: '#0088FF', flash: '#4488FF' };
      case 'red':
        return { main: '#FF6666', glow: '#FF2222', flash: '#FF4444' };
      default:
        return { main: '#FFFFFF', glow: '#DDDDFF', flash: '#FFFFFF' };
    }
  }, [color]);

  // Create explosion particles
  const explosionParticles = useMemo(() => {
    const count = 60;
    const positions = new Float32Array(count * 3);
    const velocities: THREE.Vector3[] = [];

    for (let i = 0; i < count; i++) {
      positions[i * 3] = 0;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = 0;
      const angle = Math.random() * Math.PI * 2;
      const speed = 1 + Math.random() * 3;
      velocities.push(new THREE.Vector3(
        Math.cos(angle) * speed,
        Math.random() * 5 + 2,
        Math.sin(angle) * speed
      ));
    }

    return { positions, velocities, count };
  }, []);

  // Schedule next strike
  const scheduleNextStrike = () => {
    const interval = minInterval + Math.random() * (maxInterval - minInterval);
    nextStrikeTime.current = Date.now() + interval * (1 - intensity * 0.3);
  };

  useEffect(() => {
    scheduleNextStrike();
  }, []);

  useFrame((state) => {
    const now = Date.now();
    const time = state.clock.elapsedTime;

    // Check if it's time for a new strike
    if (!isStriking && now >= nextStrikeTime.current) {
      let x: number;
      let z: number;

      if (playerPosition) {
        // Strike near the player for scary effect!
        // Random angle around the player
        const angle = Math.random() * Math.PI * 2;
        // Random distance from player (between 1 and strikeRadius units away)
        const distance = 1 + Math.random() * (strikeRadius - 1);
        // Calculate position relative to center (WeatherSystem is centered)
        x = playerPosition[0] - areaSize[0] / 2 + Math.cos(angle) * distance;
        z = playerPosition[2] - areaSize[1] / 2 + Math.sin(angle) * distance;
        // Clamp to area bounds
        x = Math.max(-areaSize[0] / 2 * 0.9, Math.min(areaSize[0] / 2 * 0.9, x));
        z = Math.max(-areaSize[1] / 2 * 0.9, Math.min(areaSize[1] / 2 * 0.9, z));
      } else {
        // Fallback to random position
        x = (Math.random() - 0.5) * areaSize[0] * 0.7;
        z = (Math.random() - 0.5) * areaSize[1] * 0.7;
      }

      setStrikePosition([x, z]);
      setLightningSegments(generateLightningSegments(x, z));
      setVisibleSegments(0);
      setIsStriking(true);
      strikeStartTime.current = time;
      strikePhase.current = 'descend';
      scheduleNextStrike();
      // Call the onStrike callback (for thunder sounds, etc.)
      onStrike?.();
    }

    if (!isStriking) return;

    const elapsed = time - strikeStartTime.current;

    // Phase timings
    const descendDuration = 0.1;
    const flashDuration = 0.2;
    const explosionDuration = 0.5;
    const fadeDuration = 0.3;

    if (elapsed < descendDuration) {
      // Descend phase - animate bolt appearing segment by segment
      strikePhase.current = 'descend';
      const progress = elapsed / descendDuration;
      const newVisibleSegments = Math.floor(progress * lightningSegments.length);
      setVisibleSegments(newVisibleSegments);
      flashIntensity.current = progress * 0.3;

    } else if (elapsed < descendDuration + flashDuration) {
      // Flash phase - maximum brightness with flicker
      strikePhase.current = 'flash';
      setVisibleSegments(lightningSegments.length);
      const flickerTime = elapsed - descendDuration;
      const flicker = Math.sin(flickerTime * 80) * 0.3 + 0.7;
      flashIntensity.current = flicker;

    } else if (elapsed < descendDuration + flashDuration + explosionDuration) {
      // Explosion phase
      strikePhase.current = 'explosion';
      const explosionTime = elapsed - descendDuration - flashDuration;
      const explosionProgress = explosionTime / explosionDuration;
      flashIntensity.current = Math.max(0, 1 - explosionProgress * 2);
      setVisibleSegments(Math.floor((1 - explosionProgress) * lightningSegments.length));

      // Update explosion particles
      if (explosionRef.current) {
        const positions = explosionRef.current.geometry.attributes.position.array as Float32Array;
        const velocities = explosionParticles.velocities;

        for (let i = 0; i < velocities.length; i++) {
          const vel = velocities[i];
          const gravity = explosionProgress * explosionProgress * 8;
          positions[i * 3] = strikePosition[0] + vel.x * explosionProgress * 1.5;
          positions[i * 3 + 1] = vel.y * explosionProgress * 1.5 - gravity;
          positions[i * 3 + 2] = strikePosition[1] + vel.z * explosionProgress * 1.5;
        }

        explosionRef.current.geometry.attributes.position.needsUpdate = true;

        const particleMaterial = explosionRef.current.material as THREE.PointsMaterial;
        particleMaterial.opacity = Math.max(0, 1 - explosionProgress * 1.2);
      }

    } else if (elapsed < descendDuration + flashDuration + explosionDuration + fadeDuration) {
      // Fade phase
      strikePhase.current = 'fade';
      flashIntensity.current = 0;
      setVisibleSegments(0);

    } else {
      // Strike complete
      setIsStriking(false);
      flashIntensity.current = 0;
      setVisibleSegments(0);
    }

    // Update screen flash
    if (flashPlaneRef.current) {
      const flashMaterial = flashPlaneRef.current.material as THREE.MeshBasicMaterial;
      flashMaterial.opacity = flashIntensity.current * intensity * 0.4;
      // Keep flash plane facing camera
      flashPlaneRef.current.quaternion.copy(camera.quaternion);
    }
  });

  const mainColor = new THREE.Color(colorConfig.main);
  const glowColor = new THREE.Color(colorConfig.glow);
  const flashColor = new THREE.Color(colorConfig.flash);

  return (
    <group ref={groupRef}>
      {/* Lightning bolt segments using cylinders */}
      {isStriking && lightningSegments.slice(0, visibleSegments).map((segment, idx) => {
        const direction = new THREE.Vector3().subVectors(segment.end, segment.start);
        const length = direction.length();
        const midpoint = new THREE.Vector3().addVectors(segment.start, segment.end).multiplyScalar(0.5);

        // Calculate rotation to align cylinder with segment
        const up = new THREE.Vector3(0, 1, 0);
        const quaternion = new THREE.Quaternion().setFromUnitVectors(up, direction.normalize());

        return (
          <group key={`bolt-${idx}`}>
            {/* Main bolt */}
            <mesh
              position={[midpoint.x, midpoint.y, midpoint.z]}
              quaternion={quaternion}
            >
              <cylinderGeometry args={[segment.thickness, segment.thickness * 0.8, length, 6]} />
              <meshBasicMaterial
                color={mainColor}
                transparent
                opacity={flashIntensity.current}
              />
            </mesh>
            {/* Glow around bolt */}
            <mesh
              position={[midpoint.x, midpoint.y, midpoint.z]}
              quaternion={quaternion}
            >
              <cylinderGeometry args={[segment.thickness * 2.5, segment.thickness * 2, length, 6]} />
              <meshBasicMaterial
                color={glowColor}
                transparent
                opacity={flashIntensity.current * 0.3}
              />
            </mesh>
          </group>
        );
      })}

      {/* Branch lightning bolts */}
      {isStriking && visibleSegments > 2 && [1, 3, 5].map((segIdx) => {
        if (segIdx >= lightningSegments.length || segIdx >= visibleSegments) return null;
        const parentSeg = lightningSegments[segIdx];
        const branchLength = 1 + Math.random();
        const branchAngle = (Math.random() - 0.5) * Math.PI;
        const branchDir = new THREE.Vector3(
          Math.sin(branchAngle) * 0.7,
          -0.7,
          Math.cos(branchAngle) * 0.7
        ).normalize();
        const branchEnd = new THREE.Vector3().copy(parentSeg.start).add(branchDir.multiplyScalar(branchLength));
        const branchMid = new THREE.Vector3().addVectors(parentSeg.start, branchEnd).multiplyScalar(0.5);

        const up = new THREE.Vector3(0, 1, 0);
        const quaternion = new THREE.Quaternion().setFromUnitVectors(up, branchDir);

        return (
          <mesh
            key={`branch-${segIdx}`}
            position={[branchMid.x, branchMid.y, branchMid.z]}
            quaternion={quaternion}
          >
            <cylinderGeometry args={[0.02, 0.015, branchLength, 4]} />
            <meshBasicMaterial
              color={glowColor}
              transparent
              opacity={flashIntensity.current * 0.6}
            />
          </mesh>
        );
      })}

      {/* Screen flash overlay */}
      <mesh ref={flashPlaneRef} position={[0, 6, 0]}>
        <planeGeometry args={[80, 80]} />
        <meshBasicMaterial
          color={flashColor}
          transparent
          opacity={0}
          side={THREE.DoubleSide}
          depthWrite={false}
        />
      </mesh>

      {/* Ground explosion particles */}
      <points ref={explosionRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={explosionParticles.count}
            array={explosionParticles.positions}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          color={mainColor}
          size={0.2}
          transparent
          opacity={0}
          sizeAttenuation
        />
      </points>

      {/* Ground impact glow */}
      {isStriking && (strikePhase.current === 'flash' || strikePhase.current === 'explosion') && (
        <mesh
          position={[strikePosition[0], 0.1, strikePosition[1]]}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <circleGeometry args={[1.5 * flashIntensity.current + 0.5, 16]} />
          <meshBasicMaterial
            color={glowColor}
            transparent
            opacity={flashIntensity.current * 0.6}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}

      {/* Impact point light */}
      {isStriking && (
        <pointLight
          position={[strikePosition[0], 2, strikePosition[1]]}
          color={mainColor}
          intensity={flashIntensity.current * 80 * intensity}
          distance={25}
          decay={2}
        />
      )}

      {/* Sky illumination during flash */}
      {isStriking && strikePhase.current === 'flash' && (
        <ambientLight color={flashColor} intensity={flashIntensity.current * 2 * intensity} />
      )}
    </group>
  );
}
